import React, { useEffect, useId, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from '../../context/Internationalization';
import LoadingButton from '../../Components/Common/LoadingButton';
import StatusAlert from '../../Components/Common/StatusAlert';
import { Button } from '../../shadcn/components/ui/button';
import { Checkbox } from '../../shadcn/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '../../shadcn/components/ui/field';
import RegistrationClubFields from './RegistrationClubFields';
import RegistrationPhotoField from './RegistrationPhotoField';
import { isRecoveringRegistration } from './registrationProgress';
import AuthTextField from '../Login/AuthTextField';

const linkClass = 'underline underline-offset-4 outline-none focus-visible:ring-3 focus-visible:ring-ring/30 rounded-sm';

export default function RegistrationForm({ values, email, errors, error, busy, progress, logoPreview, photoPreview, photoSelected,
  step, onBack, onChange, onLogo, onPhoto, onRemoveLogo, onSubmit, onSignOut }) {
  const { t } = useTranslation();
  const heading = useRef(null);
  const previousStep = useRef(step);
  const recovering = isRecoveringRegistration(progress);
  const uncertain = ['creating', 'uncertain'].includes(progress?.phase);
  const clubStep = recovering || step === 'club';
  useEffect(() => {
    if (previousStep.current !== step) {
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView?.({ block: 'nearest' });
      previousStep.current = step;
    }
  }, [step]);

  const termsId = useId();
  return <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6" aria-busy={busy} aria-labelledby="registration-heading">
    <div>
      <h1 id="registration-heading" ref={heading} tabIndex={-1} className="mb-3 outline-none">
        {t(recovering ? 'registration_finish_setup' : clubStep ? 'registration_create_club' : 'registration_create_profile')}
      </h1>
      <p className="text-sm text-muted-foreground">
        {t(clubStep ? 'registration_club_step_description' : 'registration_admin_step_description')}
      </p>
    </div>
    {error && <StatusAlert severity="error" role="alert">{t(error)}</StatusAlert>}
    {progress?.clubId && <StatusAlert severity="info">{t('registration_club_created')}</StatusAlert>}
    {clubStep ? <RegistrationClubFields values={values} errors={errors} busy={busy} progress={progress}
      logoPreview={logoPreview} onChange={onChange} onLogo={onLogo} onRemoveLogo={onRemoveLogo} /> :
      <div className="flex flex-col gap-4">
        <RegistrationPhotoField preview={photoPreview} selected={photoSelected} error={errors.photo} busy={busy} onChange={onPhoto} />
        {['firstName', 'lastName'].map((name) => <AuthTextField key={name} required
          label={t(name === 'firstName' ? 'registration_first_name' : 'registration_last_name')} name={name}
          autoComplete={name === 'firstName' ? 'given-name' : 'family-name'} value={values[name]} disabled={busy}
          onChange={(event) => onChange(name, event.target.value)} error={Boolean(errors[name])}
          helperText={errors[name] ? t(errors[name]) : ''} inputProps={{ maxLength: 100 }} />)}
        <Field orientation="horizontal" data-invalid={errors.termsAccepted ? true : undefined} className="items-start">
          <Checkbox id={termsId} checked={values.termsAccepted} disabled={busy} name="termsAccepted"
            className="mt-0.5" aria-invalid={Boolean(errors.termsAccepted)}
            aria-describedby={errors.termsAccepted ? 'registration-terms-error' : undefined}
            onCheckedChange={(checked) => onChange('termsAccepted', checked)} />
          <FieldLabel htmlFor={termsId} className="block min-w-0 flex-1 text-sm font-normal">{t('registration_agree')}{' '}
            <a href="https://www.playerdex.app/terms" target="_blank" rel="noopener noreferrer" className={linkClass}>{t('registration_terms')}</a>{' '}
            {t('registration_and')}{' '}<a href="https://www.playerdex.app/privacy" target="_blank" rel="noopener noreferrer" className={linkClass}>{t('registration_privacy')}</a>.
          </FieldLabel>
        </Field>
        {errors.termsAccepted && <FieldError id="registration-terms-error">{t(errors.termsAccepted)}</FieldError>}
      </div>}
    <div className="flex flex-col gap-4">
      <LoadingButton size="lg" type="submit" loading={busy}>
        {t(uncertain ? 'registration_check_again' : recovering ? 'registration_finish_setup' : clubStep ? 'registration_continue' : 'registration_complete_setup')}
      </LoadingButton>
      {!clubStep && <Button variant="outline" size="lg" type="button" disabled={busy} onClick={onBack}>
        <ArrowLeft aria-hidden="true" />{t('registration_back')}
      </Button>}
      <div className="flex flex-wrap items-baseline justify-center gap-1">
        <span className="min-w-0 text-sm text-muted-foreground [overflow-wrap:anywhere]"
          aria-label={`${t('registration_verified_email')}: ${email}`}>{email}</span>
        <Button variant="link" size="xs" type="button" onClick={onSignOut} disabled={busy}
          className="h-auto min-w-0 p-1 text-foreground underline">{t('registration_sign_out')}</Button>
      </div>
    </div>
  </form>;
}
